import { AddCircleOutline, ArrowBack, ArrowForward, FilterAlt, Inventory2 } from "@mui/icons-material";
import { Alert, Badge, Button, Step, StepLabel, Stepper, Tab, Tabs, Typography, useMediaQuery } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { useAtom } from "jotai";
import { CSSProperties, FC, Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { permissionsAtom } from "../../atoms/atom";
import AppBar from "../../components/app-bar/AppBar";
import { useAlert } from "../../components/contextProviders/AlertProvider";
import { getDownload } from "../../components/form/info-form-modal/InfoFormModal";
import RiskAddRowModal from "../../components/risk-assessment/RiskAddRowModal";
import RiskEpiDeliveriesModal from "../../components/risk-assessment/RiskEpiDeliveriesModal";
import RiskGrid from "../../components/risk-assessment/RiskGrid";
import RiskMissingPositionsAlert from "../../components/risk-assessment/RiskMissingPositionsAlert";
import RiskSelectionStep from "../../components/risk-assessment/RiskSelectionStep";
import RiskSpinner from "../../components/risk-assessment/RiskSpinner";
import { ALL_COLUMNS, ASSESSMENT_TAB, CA_TAB, COLUMN_BY_ID, LEFT_COLUMNS, TABS, TAB_COLUMNS, leftWidth } from "../../components/risk-assessment/riskColumns";
import {
	BulkValues, EditingCell, EditorRow, Filters, RiskGridHandlers, SelectionState, allSelectedState, appendLine, applyBulk,
	buildEditorRows, buildLinkIndex, bulkRisks, changedTabs, countLabel, createAddedRows, createLine, emptySelection, facetOptions,
	filterRows, hasFilter, insertAddedRows, invalidLinkedBulk, isRecommendedEpi, isRowChanged, isUtilizedEpi, pruneSelection, removeLine, restoreLine, revertRow, rowCheckState, setLineValue,
	setRowSelected, setRowValue, setRecommendedEpi, setRowsSelected, setUtilizedEpi, toFinishRows, toggleLineSelection, toggleRowRemoved, toggleRowSelection, withoutLine
} from "../../components/risk-assessment/riskEditorState";
import { restoreDraft, serializeDraft } from "../../components/risk-assessment/riskDraft";
import { readErrorMessage } from "../../components/risk-assessment/riskErrors";
import { useBusyAction } from "../../components/risk-assessment/useBusyAction";
import { DraftAutosave, useDraftAutosave } from "../../components/risk-assessment/useDraftAutosave";
import { useLeaveGuard } from "../../components/risk-assessment/useLeaveGuard";
import { RiskAssessmentService } from "../../services/RiskAssessmentService";
import { RiskCompanyDTO, RiskLinkTreeDTO, RiskSheetDTO } from "../../types/RiskAssessmentDTO";
import { useUtils } from "../../utils/utilFunctions";
import { getLoading } from "../execution-events/ExecutionEvents";
import './RiskAssessmentNew.scss';

type FlowStep = 'selection' | 'edit' | 'review';

const FLOW_STEPS: FlowStep[] = ['selection', 'edit', 'review'];
const FLOW_STEP_LABELS = ['Seleção da planilha', 'Edição dos dados', 'Revisão'];

const NO_OPTIONS: Record<string, string[]> = {};

const RiskAssessmentNew: FC = () => {
	const [permissions] = useAtom(permissionsAtom);
	const [step, setStep] = useState<FlowStep>('selection');
	const [companies, setCompanies] = useState<RiskCompanyDTO[]>([]);
	const [company, setCompany] = useState<RiskCompanyDTO | null>(null);
	const [file, setFile] = useState<File | null>(null);
	const [fileInputKey, setFileInputKey] = useState(0);
	const [sheet, setSheet] = useState<RiskSheetDTO | null>(null);
	const [parsing, setParsing] = useState(false);
	const [parseError, setParseError] = useState('');
	const [assessmentDate, setAssessmentDate] = useState<Dayjs | null>(null);
	const [validityDate, setValidityDate] = useState<Dayjs | null>(null);
	const [rows, setRows] = useState<EditorRow[]>([]);
	const [tab, setTab] = useState(ASSESSMENT_TAB);
	const [filters, setFilters] = useState<Filters>({});
	const [pinned, setPinned] = useState<Set<string>>(new Set());
	const [selection, setSelection] = useState<SelectionState>(emptySelection());
	const [bulk, setBulk] = useState<BulkValues>({});
	const [editing, setEditing] = useState<EditingCell | null>(null);
	const [deliveriesOpen, setDeliveriesOpen] = useState(false);
	const [addOpen, setAddOpen] = useState(false);
	const [linkTree, setLinkTree] = useState<RiskLinkTreeDTO | null>(null);
	const [finishing, setFinishing] = useState(false);
	const [loadingDraft, setLoadingDraft] = useState(false);
	const [stage, setStage] = useState<string | null>(null);
	const { draftId } = useParams();
	const requestSeq = useRef(0);
	const tabRef = useRef(tab);
	tabRef.current = tab;
	const navigate = useNavigate();
	const isMobile = useMediaQuery('(max-width:768px)');
	const { busy, run } = useBusyAction();
	const { verifyPermission } = useUtils();
	const { showAlert, showError } = useAlert();
	const service = new RiskAssessmentService();

	useEffect(() => {
		const loadCompanies = async () => {
			const response = await service.getCompanies();
			if (!response) {
				showError('Não foi possível carregar as empresas. Recarregue a página para tentar novamente.');
			}
			setCompanies(response?.data || []);
		};
		loadCompanies();
		if (!!permissions.length) {
			verifyPermission(permissions, 'RISK_ASSESSMENT');
		}
	}, []);

	useEffect(() => {
		const seq = ++requestSeq.current;
		setSheet(null);
		setParseError('');
		if (!company || !file) {
			setParsing(false);
			return;
		}
		const form = new FormData();
		form.append('file', file);
		form.append('companyId', String(company.companyId));
		setParsing(true);
		service.parse(form)
			.then(response => {
				if (seq === requestSeq.current) {
					setSheet(response.data);
				}
			})
			.catch(async error => {
				const message = await readErrorMessage(error, 'Não foi possível ler a planilha.');
				if (seq === requestSeq.current) {
					setParseError(message);
				}
			})
			.finally(() => {
				if (seq === requestSeq.current) {
					setParsing(false);
				}
			});
	}, [company, file]);

	const reviewing = step === 'review';
	const visibleRows = useMemo(() => reviewing
		? filterRows(rows, {}, ALL_COLUMNS, true)
		: filterRows(rows, filters, ALL_COLUMNS, false, pinned), [rows, filters, reviewing, pinned]);
	const changedCount = useMemo(() => rows.filter(isRowChanged).length, [rows]);
	const tabsWithChanges = useMemo(() => changedTabs(rows), [rows]);
	const snapshot = useMemo(() => serializeDraft(rows, tab, filters), [rows, tab, filters]);
	const autosave = useDraftAutosave(draftId, snapshot);
	const linkIndex = useMemo(() => buildLinkIndex(linkTree?.links || sheet?.links || [],
		[...(sheet?.catalog || []), ...(linkTree?.catalog || [])]), [sheet, linkTree]);
	const options = sheet?.options || NO_OPTIONS;
	const filterOptions = useMemo(() => facetOptions(rows, filters, ALL_COLUMNS, [...LEFT_COLUMNS, ...TAB_COLUMNS[tab]]
		.filter(item => item.kind !== 'text' && !item.multiple), pinned), [rows, filters, tab, pinned]);
	const headerCheck = useMemo(() => allSelectedState(selection, visibleRows, tab), [selection, visibleRows, tab]);
	const selectedCount = useMemo(() => visibleRows
		.filter(row => !row.removed && rowCheckState(selection, row, tab) !== 'unchecked').length, [selection, visibleRows, tab]);
	const risksForBulk = useMemo(() => bulkRisks(visibleRows, selection, tab), [visibleRows, selection, tab]);
	const canApplyBulk = selectedCount > 0 && TAB_COLUMNS[tab].some(item => !!bulk[item.id]);
	const validityError = !!assessmentDate && !!validityDate && assessmentDate.isValid() && validityDate.isValid()
		&& !validityDate.isAfter(assessmentDate, 'day');
	const datesValid = !!assessmentDate && assessmentDate.isValid() && !!validityDate && validityDate.isValid() && !validityError;
	const canStart = !!sheet && !parsing && !parseError && datesValid;

	useEffect(() => {
		if (!reviewing) {
			setSelection(previous => pruneSelection(previous, visibleRows));
		}
	}, [visibleRows, reviewing]);

	useEffect(() => {
		if (draftId) {
			loadDraft(draftId);
		}
	}, [draftId]);

	const allowLeave = useLeaveGuard(step !== 'selection' && autosave.dirty,
		'Há alterações do último minuto que ainda não foram salvas no rascunho e serão perdidas. Deseja sair mesmo assim?');

	const handlers = useMemo<RiskGridHandlers>(() => {
		const pin = (rowKey: string) => setPinned(previous => previous.has(rowKey) ? previous : new Set(previous).add(rowKey));
		return {
			onToggleRow: (row) => setSelection(previous => toggleRowSelection(previous, row, tabRef.current)),
			onToggleLine: (row, lineId) => setSelection(previous => toggleLineSelection(previous, row, tabRef.current, lineId)),
			onStartEdit: (cell) => setEditing(cell),
			onCommitEdit: (cell, value) => {
				const target = COLUMN_BY_ID[cell.columnId];
				pin(cell.rowKey);
				setRows(previous => !cell.lineId
					? setRowValue(previous, cell.rowKey, target.index, value)
					: isUtilizedEpi(target)
						? setUtilizedEpi(previous, cell.rowKey, cell.lineId, value)
						: isRecommendedEpi(target)
							? setRecommendedEpi(previous, cell.rowKey, cell.lineId, value)
							: setLineValue(previous, cell.rowKey, target.tab, cell.lineId, target.index, value));
				setEditing(null);
			},
			onCancelEdit: () => setEditing(null),
			onToggleRowRemoved: (row) => {
				pin(row.key);
				setRows(previous => toggleRowRemoved(previous, row.key));
				setSelection(previous => setRowSelected(previous, row, false));
				setEditing(null);
			},
			onAddLine: (row) => {
				const currentTab = tabRef.current;
				const line = createLine(row.key, currentTab);
				pin(row.key);
				setRows(previous => appendLine(previous, row.key, currentTab, line));
				setEditing({ rowKey: row.key, lineId: line.id, columnId: TAB_COLUMNS[currentTab][0].id });
			},
			onRemoveLine: (row, line) => {
				pin(row.key);
				setRows(previous => removeLine(previous, row.key, tabRef.current, line.id));
				setSelection(previous => withoutLine(previous, line.id));
				setEditing(null);
			},
			onRestoreLine: (row, line) => setRows(previous => restoreLine(previous, row.key, tabRef.current, line.id)),
			onRevertRow: (row) => {
				setRows(previous => revertRow(previous, row.key));
				setEditing(null);
			}
		};
	}, []);

	return (
		<Fragment>
			<AppBar
				modulo="Avaliação de riscos"
			/>
			<div className="risk-assessment-new">
				<Stepper className="etapas" activeStep={FLOW_STEPS.indexOf(step)} alternativeLabel={isMobile}>
					{FLOW_STEP_LABELS.map(label => (
						<Step key={label}>
							<StepLabel>{label}</StepLabel>
						</Step>
					))}
				</Stepper>
				{loadingDraft || (!!draftId && step === 'selection')
					? getLoading()
					: step === 'selection'
					? <RiskSelectionStep
						companies={companies}
						company={company}
						onCompanyChange={setCompany}
						file={file}
						fileInputKey={fileInputKey}
						onFileChange={changeFile}
						parsing={parsing}
						parseError={parseError}
						sheet={sheet}
						assessmentDate={assessmentDate}
						onAssessmentDateChange={setAssessmentDate}
						validityDate={validityDate}
						onValidityDateChange={setValidityDate}
						validityError={validityError}
						canStart={canStart}
						starting={busy === 'start'}
						onStart={() => run('start', start)}
					/>
					: renderEditor()
				}
			</div>
		</Fragment>
	);

	function renderEditor() {
		if (!sheet || !company || !assessmentDate || !validityDate) {
			return <></>;
		}
		return (
			<Fragment>
				<div className="cabecalho-fluxo">
					<div className="titulo">
						<Typography variant="h6">
							{reviewing ? 'Revisão — somente as linhas alteradas' : 'Nova avaliação de risco'}
						</Typography>
						<span>
							{company.companyName} - {company.companyId} · Data: {assessmentDate.format('DD/MM/YYYY')}
							{' · '}Validade: {validityDate.format('DD/MM/YYYY')}{stage ? ` · Etapa: ${stage}` : ''}
						</span>
						<span className={`salvamento ${autosave.status}`}>{saveLabel(autosave)}</span>
					</div>
					<div className="contadores">
						<span>{visibleRows.length} de {countLabel(rows.length, 'linha', 'linhas')}</span>
						{!reviewing && <span>{countLabel(selectedCount, 'selecionada', 'selecionadas')}</span>}
						<span>{countLabel(changedCount, 'alterada', 'alteradas')}</span>
					</div>
					<Button
						variant="outlined"
						startIcon={busy === 'leave' ? <RiskSpinner size={18}/> : <ArrowBack/>}
						disabled={finishing || !!busy}
						onClick={() => run('leave', leave)}
					>
						Voltar ao histórico
					</Button>
					<Button
						variant="outlined"
						startIcon={<Inventory2/>}
						disabled={finishing || !!busy}
						onClick={() => setDeliveriesOpen(true)}
					>
						Ver entregas de EPI
					</Button>
				</div>
				<RiskEpiDeliveriesModal open={deliveriesOpen} company={company} onClose={() => setDeliveriesOpen(false)}/>
				<RiskAddRowModal open={addOpen} company={company} rows={rows} onClose={() => setAddOpen(false)} onConfirm={addRows}/>
				<RiskMissingPositionsAlert positions={sheet.missingPositions} dates={sheet.sheetDates}/>
				{reviewing && !changedCount && (
					<Alert severity="info">
						Nenhuma linha alterada; a nova avaliação sai com as mesmas configurações e as novas datas.
					</Alert>
				)}
				<div className="abas" style={{ '--inicio-direita': `${leftWidth() + 2}px` } as CSSProperties}>
					<div className="acoes-grade">
						{!reviewing && (
							<Fragment>
								<Button
									size="small"
									variant="outlined"
									color="success"
									startIcon={<AddCircleOutline/>}
									disabled={!!busy}
									onClick={() => setAddOpen(true)}
								>
									Adicionar risco
								</Button>
								<Button
									size="small"
									variant="outlined"
									color="error"
									startIcon={busy === 'clearFilters' ? <RiskSpinner size={16}/> : <FilterAlt/>}
									disabled={!!busy}
									onClick={() => run('clearFilters', () => updateFilters(() => ({})))}
								>
									Limpar filtros
								</Button>
							</Fragment>
						)}
					</div>
					<Tabs
						value={tab}
						onChange={(_, next) => run(`tab:${next}`, () => changeTab(next))}
						variant="scrollable"
						scrollButtons="auto"
					>
						{TABS.map(name => (
							<Tab
								key={name}
								value={name}
								disabled={!!busy && busy !== `tab:${name}`}
								icon={busy === `tab:${name}` ? <RiskSpinner size={16}/> : undefined}
								iconPosition="start"
								label={
									<Badge
										color="warning"
										variant="dot"
										invisible={reviewing
											? !tabsWithChanges.has(name)
											: !TAB_COLUMNS[name].some(item => hasFilter(filters[item.id]))}
									>
										{name}
									</Badge>
								}
							/>
						))}
					</Tabs>
				</div>
				{tab === CA_TAB && !reviewing && (
					<Alert severity="info">
						Os CAs são atualizados automaticamente ao escolher os EPIs utilizados na aba EPI(s). Use esta aba para conferir e
						ajustar os CAs que vieram da planilha sem um EPI correspondente, como os de EPIs que foram trocados.
					</Alert>
				)}
				<RiskGrid
					rows={visibleRows}
					tab={tab}
					readOnly={reviewing}
					filters={filters}
					filterOptions={filterOptions}
					onFilterChange={(columnId, value) => updateFilters(previous => ({ ...previous, [columnId]: value }))}
					selection={selection}
					headerCheck={headerCheck}
					onToggleAll={() => run('toggleAll', toggleAll)}
					bulk={bulk}
					onBulkChange={(columnId, value) => setBulk(previous => ({ ...previous, [columnId]: value }))}
					onApplyBulk={() => run('applyBulk', applyBulkValues)}
					onClearBulk={() => run('clearBulk', clearBulkValues)}
					busy={busy}
					bulkRisks={risksForBulk}
					canApplyBulk={canApplyBulk}
					editing={editing}
					handlers={handlers}
					linkIndex={linkIndex}
					options={options}
				/>
				<div className="rodape-fluxo">
					{reviewing
						? <Fragment>
							<Button
								variant="outlined"
								startIcon={busy === 'edit' ? <RiskSpinner size={18}/> : <ArrowBack/>}
								disabled={finishing || !!busy}
								onClick={() => run('edit', () => goTo('edit'))}
							>
								Voltar para edição
							</Button>
							<Button variant="contained" color="success" disabled={finishing || !!busy} onClick={finish}>
								{finishing ? <RiskSpinner size={22}/> : 'Finalizar'}
							</Button>
						</Fragment>
						: <Button
							variant="contained"
							color="success"
							endIcon={busy === 'review' ? <RiskSpinner size={18}/> : <ArrowForward/>}
							disabled={!!busy}
							onClick={() => run('review', () => goTo('review'))}
						>
							Revisar
						</Button>
					}
				</div>
			</Fragment>
		);
	}

	async function changeFile(next: File | null) {
		if (next && !next.name.toLowerCase().endsWith('.xlsx')) {
			showError('Selecione a planilha .xlsx exportada do SGG.');
			setFile(null);
			setFileInputKey(previous => previous + 1);
			return;
		}
		if (!next) {
			setFile(null);
			return;
		}
		try {
			setFile(new File([await next.arrayBuffer()], next.name, { type: next.type }));
		} catch (error) {
			showError('Não foi possível ler o arquivo selecionado.');
		}
	}

	function updateFilters(update: (previous: Filters) => Filters) {
		setFilters(update);
		setPinned(new Set());
	}

	async function start() {
		if (!sheet || !company || !file || !assessmentDate || !validityDate) {
			return;
		}
		const form = new FormData();
		form.append('file', file);
		form.append('data', new Blob([JSON.stringify({
			companyId: company.companyId,
			assessmentDate: assessmentDate.format('DD/MM/YYYY'),
			validityDate: validityDate.format('DD/MM/YYYY')
		})], { type: 'application/json' }), 'data.json');
		try {
			const response = await service.createDraft(form);
			navigate(`/avaliacao-riscos/nova/${response.data.id}`);
		} catch (error) {
			showError(await readErrorMessage(error, 'Não foi possível iniciar a avaliação.'));
		}
	}

	async function loadDraft(id: string) {
		setLoadingDraft(true);
		try {
			const { data } = await service.getDraft(id);
			const restored = restoreDraft(buildEditorRows(data.sheet.rows, data.sheet.catalog), data.state);
			setCompany({ companyId: data.companyId, companyName: data.companyName });
			setAssessmentDate(parseBrDate(data.assessmentDate));
			setValidityDate(parseBrDate(data.validityDate));
			setStage(data.stage);
			setSheet(data.sheet);
			setRows(restored.rows);
			setTab(restored.tab);
			setFilters(restored.filters);
			setPinned(restored.changedKeys);
			setSelection(emptySelection());
			setBulk({});
			setEditing(null);
			setStep('edit');
			autosave.start(data.version, serializeDraft(restored.rows, restored.tab, restored.filters));
			if (restored.rows.some(row => row.added)) {
				const links = await service.getLinks();
				setLinkTree(links?.data || null);
			}
		} catch (error) {
			showError(await readErrorMessage(error, 'Não foi possível abrir o rascunho desta avaliação.'));
			navigate('/avaliacao-riscos');
		} finally {
			setLoadingDraft(false);
		}
	}

	async function leave() {
		await autosave.save();
		navigate('/avaliacao-riscos');
	}

	function goTo(next: FlowStep) {
		setEditing(null);
		setStep(next);
	}

	function changeTab(next: string) {
		setEditing(null);
		setTab(next);
	}

	function addRows(sector: string, positions: string[], risk: string, tree: RiskLinkTreeDTO, template: EditorRow | null) {
		const added = createAddedRows(sector, positions, risk, template || undefined);
		setLinkTree(tree);
		setRows(previous => insertAddedRows(previous, added));
		setPinned(previous => new Set([...Array.from(previous), ...added.map(row => row.key)]));
		setEditing(null);
		setAddOpen(false);
		showAlert(`${countLabel(added.length, 'risco adicionado', 'riscos adicionados')} em ${sector}.`);
	}

	function toggleAll() {
		setSelection(previous => setRowsSelected(previous, visibleRows.filter(row => !row.removed), headerCheck !== 'checked'));
	}

	function applyBulkValues() {
		const invalid = invalidLinkedBulk(bulk, TAB_COLUMNS[tab], risksForBulk, linkIndex);
		if (invalid.length) {
			showError(`O valor informado em ${invalid.map(item => item.label).join(', ')} não é permitido para os riscos `
				+ 'selecionados. Escolha novamente entre os valores permitidos.');
			return;
		}
		const result = applyBulk(rows, new Set(visibleRows.map(row => row.key)), selection, tab, TAB_COLUMNS[tab], bulk);
		setRows(result.rows);
		setPinned(previous => new Set([...Array.from(previous), ...visibleRows.map(row => row.key)]));
		if (result.addedLineIds.length) {
			setSelection(previous => ({
				rows: previous.rows,
				lines: new Set([...Array.from(previous.lines), ...result.addedLineIds])
			}));
		}
		setEditing(null);
	}

	function clearBulkValues() {
		setBulk(previous => {
			const next = { ...previous };
			TAB_COLUMNS[tab].forEach(item => delete next[item.id]);
			return next;
		});
	}

	async function finish() {
		if (!draftId || !company || !assessmentDate || !validityDate) {
			return;
		}
		setFinishing(true);
		try {
			const response = await service.finishDraft(draftId, { rows: toFinishRows(rows), state: serializeDraft(rows, tab, filters) });
			getDownload(response, `Avaliacao_${company.companyId}_${assessmentDate.format('DD-MM-YYYY')}`);
			showAlert('Avaliação gerada com sucesso. A planilha foi baixada e está disponível no histórico.');
			allowLeave();
			navigate('/avaliacao-riscos');
		} catch (error) {
			showError(await readErrorMessage(error, 'Não foi possível gerar a planilha.'));
			setFinishing(false);
		}
	}
};

function parseBrDate(value: string): Dayjs | null {
	const [day, month, year] = (value || '').split('/');
	const parsed = dayjs(`${year}-${month}-${day}`);
	return day && month && year && parsed.isValid() ? parsed : null;
}

function saveLabel(autosave: DraftAutosave): string {
	if (autosave.status === 'saving') {
		return 'Salvando rascunho...';
	}
	if (autosave.status === 'conflict' || autosave.status === 'error') {
		return autosave.message;
	}
	if (autosave.dirty) {
		return 'Alterações ainda não salvas: o rascunho é salvo automaticamente a cada minuto';
	}
	return autosave.savedAt ? `Rascunho salvo às ${dayjs(autosave.savedAt).format('HH:mm')}` : 'Rascunho salvo';
}

export default RiskAssessmentNew;
